import { logger } from "@/lib/logger";

// ExecutionPlan type (simplified for frontend use)
export interface ExecutionPlan {
  id: string;
  intent: string;
  inputs_normalized: Record<string, any>;
  computed_values?: Record<string, any>;
  risks?: string[];
  missing_fields?: string[];
  next_tool_call: { name: string; arguments: Record<string, any> };
  status: "pending" | "confirmed" | "cancelled" | "executed" | "expired";
  expires_at: string;
}

export type VoiceAgentStatus = "idle" | "recording" | "processing" | "speaking" | "error";

export interface VoiceMessage {
  role: "user" | "assistant";
  content: string;
  ts: string;
  isError?: boolean;
  showConfirmationCard?: boolean;
  suggestedReplies?: string[];
  /** URL du TTS pour jouer en même temps que le typewriter (pas après) */
  audioUrl?: string;
}

// Confirmation card data for reservations/orders
export interface ConfirmationCardData {
  type: 'order' | 'reservation';
  id: string;
  truckCount: number;
  date: string;
  firstTime: string;
  lastTime?: string;
  totalVolume?: number;
  status: string;
  // Order-specific fields
  formula?: string;
  workItems?: string[];
  truckOptions?: { truckNumber: number; options: string[] }[];
}

export interface OrderConfirmation {
  type: "order_created" | "order_cancelled" | "progress_updated";
  data: any;
  ts: string;
}

export interface UseVoiceAgentProps {
  siteId: string | undefined;
  onSessionStart?: () => void;
  onSessionEnd?: () => void;
}

// Throttling constants
export const AGENT_THROTTLE_MS = 2000;
export const QUICK_ACTION_THROTTLE_MS = 500;  // Faster for confirmations
export const SESSION_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes (increased from 3)
export const MAX_RETRIES = 3;
export const RETRY_DELAY_MS = 1000;

// Helper for retry logic
export async function withRetry<T>(
  fn: () => Promise<T>,
  retries: number = MAX_RETRIES,
  delay: number = RETRY_DELAY_MS
): Promise<T> {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === retries - 1) throw error;
      logger.warn("useVoiceAgent", `Attempt ${attempt + 1} failed, retrying...`, error);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw new Error("Max retries exceeded");
}

// Map intent to user-friendly processing message
export function getProcessingMessageForIntent(intent: string): string {
  const messages: Record<string, string> = {
    'create_concrete_pour': 'Création de la commande...',
    'reserve_trucks': 'Réservation des camions...',
    'modify_pending_order': 'Modification de la commande...',
    'complete_reservation': 'Finalisation de la réservation...',
    'modify_existing_order': 'Modification de la commande existante...',
    'calculate_work_item_volume': 'Calcul du volume...',
    'get_site_info': 'Récupération des informations...',
    'update_progress': 'Mise à jour de l\'avancement...',
    'add_work_item_comment': 'Ajout du commentaire...',
    'cancel_order_or_reservation': 'Annulation en cours...',
    'check_delivery_slip': 'Vérification du BL...',
  };
  return messages[intent] || 'Traitement en cours...';
}
